/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import SelectField from '../Common/selectField';
import { openModal } from '../../actions/modalActions';
import { getAllTargets } from '../../actions/targetActions';
import {
  removeWorkPackageFromTarget,
  addWorkPackageToTarget
} from '../../actions/workPackageActions';
import TargetRow from './targetRow';
import { targetWorkPackagesSelector } from '../../selectors/workPackageSelector';
import { filterTargetsBySpace } from '../../selectors/targetSelector';
import { WorkPackageStatuses } from '../../constants/storyStatusTypes';
import { updateUserSpaceView } from '../../actions/userActions';
import { updateUserWorkspace } from '../../utils/userHelpers';
import ErrorBoundaryWrapper from '../ErrorBoundaries/ErrorBoundaryWrapper';

const initialState = {
  tfil: '',
  pfil: '',
  gfil: '',
  sc: false,
  // open: 'YWxs',
  mod: '',
  tf: ''
};

class TargetsWall extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showCompleted: false,
      preservedState: initialState,
      filters: {},
      targetsToShow: []
    };

    this.toggleCompleted = this.toggleCompleted.bind(this);
    this.addFilter = this.addFilter.bind(this);
    this.filterTargets = this.filterTargets.bind(this);
  }

  componentWillMount() {
    this.filterTargets();
  }

  componentDidMount() {
    document.title = 'Targets';
  }

  componentDidUpdate() {
    this.filterTargets();
  }

  componentWillUnmount() {
    this.props.updateUserSpaceView({ user: updateUserWorkspace() });
  }

  addFilter(name, value) {
    const { filters } = this.state;
    if (filters[name] !== value) {
      filters[name] = value;
      this.setState({ filters });
    }
  }

  toggleCompleted() {
    this.setState((prevState) => ({ showCompleted: !prevState.showCompleted }));
  }

  filterTargets() {
    const { targetsToShow, filters } = this.state;
    const { targetWorkPackages, teams, targets } = this.props;
    const workPackages = Object.values(targetWorkPackages).reduce(
      (all, wps) => all.concat(wps),
      []
    );
    const filterByTeam = filters.team;
    // filter.team's default value will be undefined as the default filters value is an empty object
    // if All is selected from the dropdown, then the value will return a falsy 0 integer
    // if a team is selected, then that team's id will be set to filters.team to be used in the
    // following else block.

    let newTargetsToShow = [];
    if (!filterByTeam) {
      newTargetsToShow = targets.map((target) => target.id);
    } else if (filterByTeam === 'none') {
      newTargetsToShow = targets
        .filter(
          (target) =>
            !workPackages.some(
              (workPackage) => workPackage.target_id === target.id
            )
        )
        .map((target) => target.id);
      // return targets who do not have any workpackages associated with them
    } else {
      teams
        .filter((team) => (filterByTeam ? team.id === filterByTeam : true)) // finds the team set in the filter
        .forEach((team) => {
          // grab the team
          workPackages.forEach((workPackage) => {
            if (
              workPackage.team_id &&
              workPackage.team_id === team.id &&
              newTargetsToShow.indexOf(workPackage.target_id) === -1
            ) {
              // checks to see if any work packages belong to both the iterated team and a target
              newTargetsToShow.push(workPackage.target_id);
            }
          });
        });
    }
    if (JSON.stringify(newTargetsToShow) !== JSON.stringify(targetsToShow)) {
      this.setState({ targetsToShow: newTargetsToShow });
    }
  }

  generateSelectedValue = (teamOptions, filters) => {
    if (isEmpty(filters) || !filters.team) {
      return { label: '', value: null };
    }

    return teamOptions.find((option) => option.value === filters.team);
  };

  handleTeamOptionChange = (value, name) => {
    this.addFilter('team', value);
  };

  render() {
    const { targets, targetWorkPackages, teams, spaceType } = this.props;
    const {
      showCompleted,
      preservedState,
      targetsToShow,
      filters
    } = this.state;

    const teamOptions = teams.map((t) => ({
      label: t.short_name || t.name,
      value: t.id
    }));

    teamOptions.unshift({ label: 'None', value: 'none' });

    const selectedOption = this.generateSelectedValue(teamOptions, filters);

    return (
      <div className="container-fluid cadencewall planwall targetwall">
        <div className="row">
          <div className="top row">
            <div className="col-md-2">
              <button
                type="button"
                className="btn btn-link"
                onClick={this.toggleCompleted}
              >
                {showCompleted ? 'Hide' : 'Show'} Completed Targets
              </button>
            </div>
            <div className="col-md-5">
              <div className="col-md-10">
                {spaceType === 'product' ? (
                  <SelectField
                    label="Filter By Team"
                    handleChange={this.handleTeamOptionChange}
                    options={teamOptions}
                    value={selectedOption}
                    clearable
                    allowEdit
                  />
                ) : null}
              </div>
            </div>
          </div>
          <div className="row">
            {targets
              .filter((target) => targetsToShow.indexOf(target.id) !== -1)
              .map((target) => (
                <div
                  key={target.id}
                  className={
                    (target.status === WorkPackageStatuses.ACCEPTED ||
                      target.status === WorkPackageStatuses.LIVE) &&
                    !showCompleted
                      ? 'hide'
                      : 'col-md-4 cadences'
                  }
                >
                  <TargetRow
                    target={target}
                    results={targetWorkPackages[target.id] || []}
                    add={this.props.addWorkPackageToTarget}
                    remove={(workPackageId) =>
                      this.props.removeWorkPackageFromTarget(
                        workPackageId,
                        target.id
                      )
                    }
                    showCompleted={showCompleted}
                    preservedState={preservedState}
                    preserveComponentState={this.saveState}
                    startTarget={this.startTarget}
                    checkResultsToBeTransfered={this.checkResultsToBeTransfered}
                    fromWall
                  />
                </div>
              ))}
          </div>
        </div>
      </div>
    );
  }
}

TargetsWall.propTypes = {
  targets: PropTypes.array,
  openModal: PropTypes.func,
  getAllTargets: PropTypes.func,
  targetWorkPackages: PropTypes.object,
  addWorkPackageToTarget: PropTypes.func,
  removeWorkPackageFromTarget: PropTypes.func
};

const mapStateToProps = (state) => {
  const targetWorkPackages = targetWorkPackagesSelector(state);
  const { type } = state.workPackageReducer.selectedSpace;

  return {
    targetWorkPackages,
    targets: filterTargetsBySpace(state),
    teams: state.defaultOptionsReducer.currentTeams,
    spaceType: type
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      addWorkPackageToTarget,
      removeWorkPackageFromTarget,
      getAllTargets,
      openModal,
      updateUserSpaceView
    },
    dispatch
  );

export default ErrorBoundaryWrapper(
  connect(mapStateToProps, mapDispatchToProps)(TargetsWall)
);
